import React, { FC, useMemo } from "react"
import {
  Typography,
  Stack,
  Link,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  CloseIcon,
  Button
} from "@carrotfertility/carotene-core"
import { DbgPaths } from "../../../../utils/Paths"
import { FormattedMessage, useIntl } from "react-intl"
import { EMDTableContainer } from "./EMDTable"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useBenefitEnrollment } from "#/components/context/enrollment/BenefitEnrollmentContext"

type ExternalDeductibleInformationalProps = { hasCostShare: boolean; onClose?: () => void }

const ExternalDeductibleInformational: FC<ExternalDeductibleInformationalProps> = ({ onClose }) => {
  const { selectedTradingPartnerPk } = useCurrentUser()
  const { availableTradingPartners } = useBenefitEnrollment()
  const intl = useIntl()

  const tradingPartnerName = useMemo(() => {
    if (!selectedTradingPartnerPk || !availableTradingPartners) {
      return "(Error determining carrier)"
    }
    return (
      availableTradingPartners.find((tp) => tp.tradingPartnerPk === selectedTradingPartnerPk)?.description ||
      "(Error determining carrier)"
    )
  }, [selectedTradingPartnerPk, availableTradingPartners])

  return (
    <>
      {onClose && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton aria-label={intl.formatMessage({ defaultMessage: "close" })} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <DialogTitle id="deductible-modal-header">
        <FormattedMessage defaultMessage="Deductible" />
      </DialogTitle>
      <DialogContent>
        <Stack display="flex" gap={(theme) => theme.tokens.spacing.md}>
          <Typography>
            <FormattedMessage
              defaultMessage="Once you reach your health insurance deductible, you can use Carrot funds for eligible <link>qualified medical expenses</link> (QMEs) (in this category). Carrot funds you use may also be subject to bank fees, taxes, and other plan details."
              values={{
                link: (linkContent) => (
                  <Link href={DbgPaths.QUALIFIED_MEDICAL_EXPENSES} color={"inherit"} target="_blank">
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <Typography>
            <FormattedMessage
              defaultMessage="<link>Learn more about deductibles</link>"
              values={{
                link: (linkContent) => (
                  <Link href={DbgPaths.DEDUCTIBLES} color={"inherit"} target="_blank">
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <EMDTableContainer />
          <Typography fontStyle="italic">
            <FormattedMessage
              defaultMessage="* Always refer to your {carrierName} insurance portal for the most updated deductible information. Claim processing times vary by insurance provider."
              values={{ carrierName: tradingPartnerName }}
            />
          </Typography>
        </Stack>
      </DialogContent>
      {onClose && (
        <DialogActions>
          <Button onClick={onClose}>
            <FormattedMessage defaultMessage="Close" />
          </Button>
        </DialogActions>
      )}
    </>
  )
}

export { ExternalDeductibleInformational }
